import { useEffect, useRef, useState } from 'react';

/**
 * Hook for scroll-triggered animations using Intersection Observer
 * @param {Object} options - Intersection Observer options
 * @param {number} options.threshold - Visibility threshold (0-1)
 * @param {string} options.rootMargin - Root margin for earlier/later triggering
 * @returns {[React.RefObject, boolean]} - Ref to attach to element and visibility state
 */
export const useScrollAnimation = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const threshold = options.threshold ?? 0.1;
  const rootMargin = options.rootMargin ?? '0px 0px -50px 0px';

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Once visible, stay visible (one-time animation)
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold,
        rootMargin,
      }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      observer.disconnect();
    };
  }, [threshold, rootMargin]);

  return [ref, isVisible];
};

/**
 * Component wrapper for scroll animations
 */
export const AnimateOnScroll = ({ 
  children, 
  className = '', 
  animation = 'fade-up', // 'fade-up', 'fade-left', 'fade-right', 'scale'
  delay = 0,
  threshold = 0.1,
  as: Component = 'div'
}) => {
  const [ref, isVisible] = useScrollAnimation({ threshold });

  const animationClasses = {
    'fade-up': 'animate-on-scroll',
    'fade-left': 'animate-on-scroll from-left',
    'fade-right': 'animate-on-scroll from-right',
    'scale': 'animate-on-scroll scale-up',
  };

  const delayClass = delay > 0 ? `delay-${Math.min(delay, 5)}` : '';

  return (
    <Component
      ref={ref}
      className={`${animationClasses[animation] || animationClasses['fade-up']} ${delayClass} ${isVisible ? 'is-visible' : ''} ${className}`}
    >
      {children}
    </Component>
  );
};

/**
 * Initialize scroll animations for elements with data-animate attribute
 * Call this once in your app
 */
export const initScrollAnimations = () => {
  if (typeof window === 'undefined') return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    },
    {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    }
  );

  // Observe all elements with animation classes
  document.querySelectorAll('.animate-on-scroll, [data-animate-section]').forEach((el) => {
    observer.observe(el);
  });

  return observer;
};

export default useScrollAnimation;
